import { PublicKey, SystemProgram } from '@solana/web3.js';
import {
  TOKEN_PROGRAM_ID,
  ASSOCIATED_TOKEN_PROGRAM_ID,
  getAssociatedTokenAddressSync,
} from '@solana/spl-token';
import { solanaPDA } from './pda';
import {
  TOKEN_METADATA_PROGRAM_ID,
  getCollectionMetadataPDA,
  getCollectionMasterEditionPDA,
  getCollectionAuthorityRecordPDA,
  getNFTMetadataPDA,
  getNFTMasterEditionPDA,
} from '../../utils/solana-collection';

export type BlockhashHash = string | number | bigint;
type AccountMap = Record<string, PublicKey>;

// Sysvar Instructions Program ID
const SYSVAR_INSTRUCTIONS_PUBKEY = new PublicKey(
  'Sysvar1nstructions1111111111111111111111111',
);

export function buildClaimLixiAccounts(params: {
  wallet: PublicKey;
  groupOptions: { buyer: PublicKey; blockhashHash: BlockhashHash };
  opts?: {
    extraTokenMint?: PublicKey;
    vaultAuthority?: PublicKey;
    tokenVaultAta?: PublicKey;
    receiverTokenAta?: PublicKey;
    ticketId?: number;
    nftMint?: PublicKey;
    nftMetadata?: PublicKey;
    nftMasterEdition?: PublicKey;
    receiverNftTokenAccount?: PublicKey;
    collectionMint?: PublicKey;
  };
}) {
  if (!params?.groupOptions?.blockhashHash || !params?.groupOptions?.buyer) {
    throw new Error(
      '[buildClaimLixiAccounts] Missing blockhashHash hoặc buyer cho buy_tickets_auto PDA',
    );
  }
  const [globalStatePDA] = solanaPDA.getGlobalStatePDA();
  const [groupStatePDA] = solanaPDA.getGroupStatePDAForAuto(
    params.groupOptions.buyer,
    params.groupOptions.blockhashHash,
  );
  const [jackpotVaultPDA] = solanaPDA.getJackpotVaultPDA();
  const [collectionMint] = solanaPDA.getCollectionMintPDA();

  const accounts: AccountMap = {
    globalState: globalStatePDA,
    groupState: groupStatePDA,
    receiver: params.wallet,
    jackpotVault: jackpotVaultPDA,
    systemProgram: SystemProgram.programId,
  };

  if (
    params.opts?.extraTokenMint &&
    params.opts.extraTokenMint.toString() !== PublicKey.default.toString()
  ) {
    accounts.extraTokenMint = params.opts.extraTokenMint;
    accounts.vaultAuthority = params.opts.vaultAuthority ?? PublicKey.default;
    accounts.tokenVaultAta = params.opts.tokenVaultAta ?? PublicKey.default;
    accounts.receiverTokenAta =
      params.opts.receiverTokenAta ?? PublicKey.default;
  } else {
    accounts.extraTokenMint = globalStatePDA;
    accounts.vaultAuthority = jackpotVaultPDA;
    accounts.tokenVaultAta = jackpotVaultPDA;
    accounts.receiverTokenAta = params.wallet;
  }
  accounts.tokenProgram = TOKEN_PROGRAM_ID;

  // Collection accounts
  const finalCollectionMint = params.opts?.collectionMint ?? collectionMint;
  const collectionMetadata = getCollectionMetadataPDA(finalCollectionMint);
  const collectionMasterEdition = getCollectionMasterEditionPDA(finalCollectionMint);
  const collectionAuthorityRecord = getCollectionAuthorityRecordPDA(
    finalCollectionMint,
    globalStatePDA,
  );

  accounts.collectionMint = finalCollectionMint;
  accounts.collectionMetadata = collectionMetadata;
  accounts.collectionMasterEdition = collectionMasterEdition;
  accounts.collectionAuthorityRecord = collectionAuthorityRecord;
  accounts.tokenMetadataProgram = TOKEN_METADATA_PROGRAM_ID;
  accounts.associatedTokenProgram = ASSOCIATED_TOKEN_PROGRAM_ID;
  accounts.sysvarInstructions = SYSVAR_INSTRUCTIONS_PUBKEY;

  // NFT accounts for the ticket being claimed
  if (params.opts?.ticketId !== undefined) {
    const nftMint = params.opts.nftMint ?? solanaPDA.getNFTMintPDA(params.opts.ticketId)[0];
    const receiverNftTokenAccount = params.opts.receiverNftTokenAccount ?? 
      getAssociatedTokenAddressSync(nftMint, params.wallet);

    accounts.nftMint = nftMint;
    accounts.receiverNftTokenAccount = receiverNftTokenAccount;
    accounts.nftMetadata = params.opts.nftMetadata ?? getNFTMetadataPDA(nftMint);
    accounts.nftMasterEdition = params.opts.nftMasterEdition ?? getNFTMasterEditionPDA(nftMint);
  } else {
    accounts.nftMint = PublicKey.default;
    accounts.nftMetadata = PublicKey.default;
    accounts.nftMasterEdition = PublicKey.default;
    accounts.receiverNftTokenAccount = PublicKey.default;
  }

  return accounts;
}

export function buildClaimLixiByOwnerAccounts(params: {
  wallet: PublicKey;
  receiver: PublicKey;
  groupOptions: { buyer: PublicKey; blockhashHash: BlockhashHash };
  opts?: {
    extraTokenMint?: PublicKey;
    vaultAuthority?: PublicKey;
    tokenVaultAta?: PublicKey;
    receiverTokenAta?: PublicKey;
    ticketId?: number;
    nftMint?: PublicKey;
    nftMetadata?: PublicKey;
    nftMasterEdition?: PublicKey;
    receiverNftTokenAccount?: PublicKey;
    collectionMint?: PublicKey;
  };
}) {
  if (!params?.groupOptions?.blockhashHash || !params?.groupOptions?.buyer) {
    throw new Error(
      '[buildClaimLixiByOwnerAccounts] Missing blockhashHash hoặc buyer cho buy_tickets_auto PDA',
    );
  }
  if (!params.wallet.equals(params.groupOptions.buyer)) {
    throw new Error(
      '[buildClaimLixiByOwnerAccounts] Wallet must be the group owner (buyer)',
    );
  }
  const [globalStatePDA] = solanaPDA.getGlobalStatePDA();
  const [groupStatePDA] = solanaPDA.getGroupStatePDAForAuto(
    params.groupOptions.buyer,
    params.groupOptions.blockhashHash,
  );
  const [jackpotVaultPDA] = solanaPDA.getJackpotVaultPDA();
  const [collectionMint] = solanaPDA.getCollectionMintPDA();

  const accounts: AccountMap = {
    globalState: globalStatePDA,
    groupState: groupStatePDA,
    owner: params.wallet,
    receiver: params.receiver,
    jackpotVault: jackpotVaultPDA,
    systemProgram: SystemProgram.programId,
  };

  if (
    params.opts?.extraTokenMint &&
    params.opts.extraTokenMint.toString() !== PublicKey.default.toString()
  ) {
    accounts.extraTokenMint = params.opts.extraTokenMint;
    accounts.vaultAuthority = params.opts.vaultAuthority ?? PublicKey.default;
    accounts.tokenVaultAta = params.opts.tokenVaultAta ?? PublicKey.default;
    accounts.receiverTokenAta =
      params.opts.receiverTokenAta ?? PublicKey.default;
  } else {
    accounts.extraTokenMint = globalStatePDA;
    accounts.vaultAuthority = jackpotVaultPDA;
    accounts.tokenVaultAta = jackpotVaultPDA;
    accounts.receiverTokenAta = params.receiver;
  }
  accounts.tokenProgram = TOKEN_PROGRAM_ID;

  // Collection accounts
  const finalCollectionMint = params.opts?.collectionMint ?? collectionMint;
  const collectionMetadata = getCollectionMetadataPDA(finalCollectionMint);
  const collectionMasterEdition = getCollectionMasterEditionPDA(finalCollectionMint);
  const collectionAuthorityRecord = getCollectionAuthorityRecordPDA(
    finalCollectionMint,
    globalStatePDA,
  );

  accounts.collectionMint = finalCollectionMint;
  accounts.collectionMetadata = collectionMetadata;
  accounts.collectionMasterEdition = collectionMasterEdition;
  accounts.collectionAuthorityRecord = collectionAuthorityRecord;
  accounts.tokenMetadataProgram = TOKEN_METADATA_PROGRAM_ID;
  accounts.associatedTokenProgram = ASSOCIATED_TOKEN_PROGRAM_ID;
  accounts.sysvarInstructions = SYSVAR_INSTRUCTIONS_PUBKEY;

  // NFT accounts for the ticket being claimed
  if (params.opts?.ticketId !== undefined) {
    const resolvedNftMint =
      solanaPDA.getNFTMintPDA(params.opts.ticketId)[0];
    const resolvedReceiverNftAta = getAssociatedTokenAddressSync(
      resolvedNftMint,
      params.receiver,
    );

    accounts.nftMint = resolvedNftMint;
    accounts.receiverNftTokenAccount = resolvedReceiverNftAta;
    accounts.nftMetadata =
      params.opts.nftMetadata ?? getNFTMetadataPDA(resolvedNftMint);
    accounts.nftMasterEdition =
      params.opts.nftMasterEdition ?? getNFTMasterEditionPDA(resolvedNftMint);
  } else {
    accounts.nftMint = PublicKey.default;
    accounts.nftMetadata = PublicKey.default;
    accounts.nftMasterEdition = PublicKey.default;
    accounts.receiverNftTokenAccount = PublicKey.default;
  }

  return accounts;
}

export function buildClaimLixiByAdminAccounts(params: {
  wallet: PublicKey;
  receiver: PublicKey;
  groupOptions: { buyer: PublicKey; blockhashHash: BlockhashHash };
  opts?: {
    extraTokenMint?: PublicKey;
    vaultAuthority?: PublicKey;
    tokenVaultAta?: PublicKey;
    receiverTokenAta?: PublicKey;
    ticketId?: number;
    nftMint?: PublicKey;
    nftMetadata?: PublicKey;
    nftMasterEdition?: PublicKey;
    receiverNftTokenAccount?: PublicKey;
    collectionMint?: PublicKey;
    admin?: PublicKey;
  };
}) {
  if (!params?.groupOptions?.blockhashHash || !params?.groupOptions?.buyer) {
    throw new Error(
      '[buildClaimLixiByAdminAccounts] Missing blockhashHash hoặc buyer cho buy_tickets_auto PDA',
    );
  }
  const [globalStatePDA] = solanaPDA.getGlobalStatePDA();
  const [groupStatePDA] = solanaPDA.getGroupStatePDAForAuto(
    params.groupOptions.buyer,
    params.groupOptions.blockhashHash,
  );
  const [jackpotVaultPDA] = solanaPDA.getJackpotVaultPDA();
  const [collectionMint] = solanaPDA.getCollectionMintPDA();

  const accounts: AccountMap = {
    globalState: globalStatePDA,
    groupState: groupStatePDA,
    admin: params.wallet,
    receiver: params.receiver,
    jackpotVault: jackpotVaultPDA,
    systemProgram: SystemProgram.programId,
  };

  if (
    params.opts?.extraTokenMint &&
    params.opts.extraTokenMint.toString() !== PublicKey.default.toString()
  ) {
    accounts.extraTokenMint = params.opts.extraTokenMint;
    accounts.vaultAuthority = params.opts.vaultAuthority ?? PublicKey.default;
    accounts.tokenVaultAta = params.opts.tokenVaultAta ?? PublicKey.default;
    accounts.receiverTokenAta =
      params.opts.receiverTokenAta ?? PublicKey.default;
  } else {
    accounts.extraTokenMint = globalStatePDA;
    accounts.vaultAuthority = jackpotVaultPDA;
    accounts.tokenVaultAta = jackpotVaultPDA;
    accounts.receiverTokenAta = params.receiver;
  }
  accounts.tokenProgram = TOKEN_PROGRAM_ID;

  // Collection accounts
  const finalCollectionMint = params.opts?.collectionMint ?? collectionMint;
  const collectionMetadata = getCollectionMetadataPDA(finalCollectionMint);
  const collectionMasterEdition = getCollectionMasterEditionPDA(finalCollectionMint);
  const collectionAuthorityRecord = getCollectionAuthorityRecordPDA(
    finalCollectionMint,
    globalStatePDA,
  );

  accounts.collectionMint = finalCollectionMint;
  accounts.collectionMetadata = collectionMetadata;
  accounts.collectionMasterEdition = collectionMasterEdition;
  accounts.collectionAuthorityRecord = collectionAuthorityRecord;
  accounts.admin = params.opts?.admin ?? params.wallet; // Admin is signer and payer
  accounts.tokenMetadataProgram = TOKEN_METADATA_PROGRAM_ID;
  accounts.associatedTokenProgram = ASSOCIATED_TOKEN_PROGRAM_ID;
  accounts.sysvarInstructions = SYSVAR_INSTRUCTIONS_PUBKEY;

  // NFT accounts for the ticket being claimed
  if (params.opts?.ticketId !== undefined) {
    const nftMint = params.opts.nftMint ?? solanaPDA.getNFTMintPDA(params.opts.ticketId)[0];
    const receiverNftTokenAccount = params.opts.receiverNftTokenAccount ?? 
      getAssociatedTokenAddressSync(nftMint, params.receiver);

    accounts.nftMint = nftMint;
    accounts.receiverNftTokenAccount = receiverNftTokenAccount;
    accounts.nftMetadata = params.opts.nftMetadata ?? getNFTMetadataPDA(nftMint);
    accounts.nftMasterEdition = params.opts.nftMasterEdition ?? getNFTMasterEditionPDA(nftMint);
  } else {
    accounts.nftMint = PublicKey.default;
    accounts.nftMetadata = PublicKey.default;
    accounts.nftMasterEdition = PublicKey.default;
    accounts.receiverNftTokenAccount = PublicKey.default;
  }

  return accounts;
}

export function buildClaimManyByOwnerAccounts(params: {
  wallet: PublicKey;
  groupOptions: { buyer: PublicKey; blockhashHash: BlockhashHash };
  receivers: PublicKey[];
  numReceivers: number;
  opts?: {
    extraTokenMint?: PublicKey;
    vaultAuthority?: PublicKey;
    tokenVaultAta?: PublicKey;
    receiverTokenAtas?: PublicKey[];
    ticketIds?: number[];
    nftMints?: PublicKey[];
    nftMetadatas?: PublicKey[];
    nftMasterEditions?: PublicKey[];
    receiverNftTokenAccounts?: PublicKey[];
    collectionMint?: PublicKey;
  };
}) {
  if (!params?.groupOptions?.blockhashHash || !params?.groupOptions?.buyer) {
    throw new Error(
      '[buildClaimManyByOwnerAccounts] Missing blockhashHash hoặc buyer cho buy_tickets_auto PDA',
    );
  }
  if (!params.wallet.equals(params.groupOptions.buyer)) {
    throw new Error(
      '[buildClaimManyByOwnerAccounts] Wallet must be the group owner (buyer)',
    );
  }
  if (params.numReceivers < 1 || params.numReceivers > 5) {
    throw new Error('numReceivers must be between 1 and 5');
  }
  if (params.receivers.length < params.numReceivers) {
    throw new Error(
      `Receivers array length (${params.receivers.length}) is less than numReceivers (${params.numReceivers})`,
    );
  }

  const [globalStatePDA] = solanaPDA.getGlobalStatePDA();
  const [groupStatePDA] = solanaPDA.getGroupStatePDAForAuto(
    params.groupOptions.buyer,
    params.groupOptions.blockhashHash,
  );
  const [jackpotVaultPDA] = solanaPDA.getJackpotVaultPDA();
  const [collectionMint] = solanaPDA.getCollectionMintPDA();

  const paddedReceivers = [...params.receivers];
  while (paddedReceivers.length < 5) {
    paddedReceivers.push(params.receivers[0] || PublicKey.default);
  }

  const accounts: AccountMap = {
    globalState: globalStatePDA,
    groupState: groupStatePDA,
    owner: params.wallet,
    jackpotVault: jackpotVaultPDA,
    receiver1: paddedReceivers[0],
    receiver2: paddedReceivers[1],
    receiver3: paddedReceivers[2],
    receiver4: paddedReceivers[3],
    receiver5: paddedReceivers[4],
    systemProgram: SystemProgram.programId,
  };

  if (params.opts?.extraTokenMint) {
    accounts.extraTokenMint = params.opts.extraTokenMint;
    accounts.vaultAuthority = params.opts.vaultAuthority ?? PublicKey.default;
    accounts.tokenVaultAta = params.opts.tokenVaultAta ?? PublicKey.default;
    const paddedAtas = params.opts.receiverTokenAtas
      ? [...params.opts.receiverTokenAtas]
      : [];
    while (paddedAtas.length < 5) {
      paddedAtas.push(paddedAtas[0] || groupStatePDA);
    }
    accounts.receiver1TokenAta = paddedAtas[0];
    accounts.receiver2TokenAta = paddedAtas[1];
    accounts.receiver3TokenAta = paddedAtas[2];
    accounts.receiver4TokenAta = paddedAtas[3];
    accounts.receiver5TokenAta = paddedAtas[4];
    accounts.tokenProgram = TOKEN_PROGRAM_ID;
  } else {
    accounts.extraTokenMint = PublicKey.default;
    accounts.vaultAuthority = PublicKey.default;
    accounts.tokenVaultAta = groupStatePDA;
    accounts.receiver1TokenAta = groupStatePDA;
    accounts.receiver2TokenAta = groupStatePDA;
    accounts.receiver3TokenAta = groupStatePDA;
    accounts.receiver4TokenAta = groupStatePDA;
    accounts.receiver5TokenAta = groupStatePDA;
    accounts.tokenProgram = TOKEN_PROGRAM_ID;
  }

  // Collection accounts
  const finalCollectionMint = params.opts?.collectionMint ?? collectionMint;
  const collectionMetadata = getCollectionMetadataPDA(finalCollectionMint);
  const collectionMasterEdition = getCollectionMasterEditionPDA(finalCollectionMint);
  const collectionAuthorityRecord = getCollectionAuthorityRecordPDA(
    finalCollectionMint,
    globalStatePDA,
  );

  accounts.collectionMint = finalCollectionMint;
  accounts.collectionMetadata = collectionMetadata;
  accounts.collectionMasterEdition = collectionMasterEdition;
  accounts.collectionAuthorityRecord = collectionAuthorityRecord;
  accounts.tokenMetadataProgram = TOKEN_METADATA_PROGRAM_ID;
  accounts.associatedTokenProgram = ASSOCIATED_TOKEN_PROGRAM_ID;
  accounts.sysvarInstructions = SYSVAR_INSTRUCTIONS_PUBKEY;

  // NFT accounts for each receiver
  if (params.opts?.ticketIds && params.opts.ticketIds.length >= params.numReceivers) {
    const fallbackTicketId = params.opts.ticketIds[0];
    const fallbackMint =
      params.opts.nftMints?.[0] ?? solanaPDA.getNFTMintPDA(fallbackTicketId)[0];
    const fallbackReceiver = paddedReceivers[0];
    const fallbackReceiverAta =
      params.opts.receiverNftTokenAccounts?.[0] ??
      getAssociatedTokenAddressSync(fallbackMint, fallbackReceiver);
    const fallbackMetadata =
      params.opts.nftMetadatas?.[0] ?? getNFTMetadataPDA(fallbackMint);
    const fallbackMasterEdition =
      params.opts.nftMasterEditions?.[0] ?? getNFTMasterEditionPDA(fallbackMint);

    for (let i = 0; i < 5; i++) {
      const receiver = paddedReceivers[i];
      const ticketId = i < params.numReceivers ? params.opts.ticketIds[i] : fallbackTicketId;
      if (ticketId === undefined) {
        accounts[`nftMint${i + 1}`] = PublicKey.default;
        accounts[`receiver${i + 1}NftTokenAccount`] = PublicKey.default;
        accounts[`nftMetadata${i + 1}`] = PublicKey.default;
        accounts[`nftMasterEdition${i + 1}`] = PublicKey.default;
        continue;
      }

      const nftMint =
        i < params.numReceivers
          ? params.opts.nftMints?.[i] ?? solanaPDA.getNFTMintPDA(ticketId)[0]
          : fallbackMint;
      const receiverNftTokenAccount =
        i < params.numReceivers
          ? params.opts.receiverNftTokenAccounts?.[i] ??
            getAssociatedTokenAddressSync(nftMint, receiver)
          : fallbackReceiverAta;

      accounts[`nftMint${i + 1}`] = nftMint;
      accounts[`receiver${i + 1}NftTokenAccount`] = receiverNftTokenAccount;
      accounts[`nftMetadata${i + 1}`] =
        i < params.numReceivers
          ? params.opts.nftMetadatas?.[i] ?? getNFTMetadataPDA(nftMint)
          : fallbackMetadata;
      accounts[`nftMasterEdition${i + 1}`] =
        i < params.numReceivers
          ? params.opts.nftMasterEditions?.[i] ?? getNFTMasterEditionPDA(nftMint)
          : fallbackMasterEdition;
    }
  } else {
    // No ticket IDs provided - use default values
    for (let i = 0; i < 5; i++) {
      accounts[`nftMint${i + 1}`] = PublicKey.default;
      accounts[`receiver${i + 1}NftTokenAccount`] = PublicKey.default;
      accounts[`nftMetadata${i + 1}`] = PublicKey.default;
      accounts[`nftMasterEdition${i + 1}`] = PublicKey.default;
    }
  }

  return accounts;
}

export function buildClaimManyByAdminAccounts(params: {
  wallet: PublicKey;
  groupOptions: { buyer: PublicKey; blockhashHash: BlockhashHash };
  receivers: PublicKey[];
  numReceivers: number;
  opts?: {
    extraTokenMint?: PublicKey;
    vaultAuthority?: PublicKey;
    tokenVaultAta?: PublicKey;
    receiverTokenAtas?: PublicKey[];
    ticketIds?: number[];
    nftMints?: PublicKey[];
    nftMetadatas?: PublicKey[];
    nftMasterEditions?: PublicKey[];
    receiverNftTokenAccounts?: PublicKey[];
    collectionMint?: PublicKey;
    admin?: PublicKey;
  };
}) {
  if (!params?.groupOptions?.blockhashHash || !params?.groupOptions?.buyer) {
    throw new Error(
      '[buildClaimManyByAdminAccounts] Missing blockhashHash hoặc buyer cho buy_tickets_auto PDA',
    );
  }
  if (params.numReceivers < 1 || params.numReceivers > 5) {
    throw new Error('numReceivers must be between 1 and 5');
  }
  if (params.receivers.length < params.numReceivers) {
    throw new Error(
      `Receivers array length (${params.receivers.length}) is less than numReceivers (${params.numReceivers})`,
    );
  }

  const [globalStatePDA] = solanaPDA.getGlobalStatePDA();
  const [groupStatePDA] = solanaPDA.getGroupStatePDAForAuto(
    params.groupOptions.buyer,
    params.groupOptions.blockhashHash,
  );
  const [jackpotVaultPDA] = solanaPDA.getJackpotVaultPDA();
  const [collectionMint] = solanaPDA.getCollectionMintPDA();

  const paddedReceivers = [...params.receivers];
  while (paddedReceivers.length < 5) {
    paddedReceivers.push(params.receivers[0] || PublicKey.default);
  }

  const accounts: AccountMap = {
    globalState: globalStatePDA,
    groupState: groupStatePDA,
    admin: params.wallet,
    jackpotVault: jackpotVaultPDA,
    receiver1: paddedReceivers[0],
    receiver2: paddedReceivers[1],
    receiver3: paddedReceivers[2],
    receiver4: paddedReceivers[3],
    receiver5: paddedReceivers[4],
    systemProgram: SystemProgram.programId,
  };

  if (params.opts?.extraTokenMint) {
    accounts.extraTokenMint = params.opts.extraTokenMint;
    accounts.vaultAuthority = params.opts.vaultAuthority ?? PublicKey.default;
    accounts.tokenVaultAta = params.opts.tokenVaultAta ?? PublicKey.default;
    const paddedAtas = params.opts.receiverTokenAtas
      ? [...params.opts.receiverTokenAtas]
      : [];
    while (paddedAtas.length < 5) {
      paddedAtas.push(paddedAtas[0] || groupStatePDA);
    }
    accounts.receiver1TokenAta = paddedAtas[0];
    accounts.receiver2TokenAta = paddedAtas[1];
    accounts.receiver3TokenAta = paddedAtas[2];
    accounts.receiver4TokenAta = paddedAtas[3];
    accounts.receiver5TokenAta = paddedAtas[4];
    accounts.tokenProgram = TOKEN_PROGRAM_ID;
  } else {
    accounts.extraTokenMint = PublicKey.default;
    accounts.vaultAuthority = PublicKey.default;
    accounts.tokenVaultAta = groupStatePDA;
    accounts.receiver1TokenAta = groupStatePDA;
    accounts.receiver2TokenAta = groupStatePDA;
    accounts.receiver3TokenAta = groupStatePDA;
    accounts.receiver4TokenAta = groupStatePDA;
    accounts.receiver5TokenAta = groupStatePDA;
    accounts.tokenProgram = TOKEN_PROGRAM_ID;
  }

  // Collection accounts
  const finalCollectionMint = params.opts?.collectionMint ?? collectionMint;
  const collectionMetadata = getCollectionMetadataPDA(finalCollectionMint);
  const collectionMasterEdition = getCollectionMasterEditionPDA(finalCollectionMint);
  const collectionAuthorityRecord = getCollectionAuthorityRecordPDA(
    finalCollectionMint,
    globalStatePDA,
  );

  accounts.collectionMint = finalCollectionMint;
  accounts.collectionMetadata = collectionMetadata;
  accounts.collectionMasterEdition = collectionMasterEdition;
  accounts.collectionAuthorityRecord = collectionAuthorityRecord;
  accounts.admin = params.opts?.admin ?? params.wallet; // Admin is signer and payer
  accounts.tokenMetadataProgram = TOKEN_METADATA_PROGRAM_ID;
  accounts.associatedTokenProgram = ASSOCIATED_TOKEN_PROGRAM_ID;
  accounts.sysvarInstructions = SYSVAR_INSTRUCTIONS_PUBKEY;

  // NFT accounts for each receiver
  if (params.opts?.ticketIds && params.opts.ticketIds.length >= params.numReceivers) {
    const fallbackTicketId = params.opts.ticketIds[0];
    const fallbackMint =
      params.opts.nftMints?.[0] ?? solanaPDA.getNFTMintPDA(fallbackTicketId)[0];
    const fallbackReceiver = paddedReceivers[0];
    const fallbackReceiverAta =
      params.opts.receiverNftTokenAccounts?.[0] ??
      getAssociatedTokenAddressSync(fallbackMint, fallbackReceiver);
    const fallbackMetadata =
      params.opts.nftMetadatas?.[0] ?? getNFTMetadataPDA(fallbackMint);
    const fallbackMasterEdition =
      params.opts.nftMasterEditions?.[0] ?? getNFTMasterEditionPDA(fallbackMint);

    for (let i = 0; i < 5; i++) {
      const receiver = paddedReceivers[i];
      const ticketId = i < params.numReceivers ? params.opts.ticketIds[i] : fallbackTicketId;
      if (ticketId === undefined) {
        accounts[`nftMint${i + 1}`] = PublicKey.default;
        accounts[`receiver${i + 1}NftTokenAccount`] = PublicKey.default;
        accounts[`nftMetadata${i + 1}`] = PublicKey.default;
        accounts[`nftMasterEdition${i + 1}`] = PublicKey.default;
        continue;
      }

      const nftMint =
        i < params.numReceivers
          ? params.opts.nftMints?.[i] ?? solanaPDA.getNFTMintPDA(ticketId)[0]
          : fallbackMint;
      const receiverNftTokenAccount =
        i < params.numReceivers
          ? params.opts.receiverNftTokenAccounts?.[i] ??
            getAssociatedTokenAddressSync(nftMint, receiver)
          : fallbackReceiverAta;

      accounts[`nftMint${i + 1}`] = nftMint;
      accounts[`receiver${i + 1}NftTokenAccount`] = receiverNftTokenAccount;
      accounts[`nftMetadata${i + 1}`] =
        i < params.numReceivers
          ? params.opts.nftMetadatas?.[i] ?? getNFTMetadataPDA(nftMint)
          : fallbackMetadata;
      accounts[`nftMasterEdition${i + 1}`] =
        i < params.numReceivers
          ? params.opts.nftMasterEditions?.[i] ?? getNFTMasterEditionPDA(nftMint)
          : fallbackMasterEdition;
    }
  } else {
    // No ticket IDs provided - use default values
    for (let i = 0; i < 5; i++) {
      accounts[`nftMint${i + 1}`] = PublicKey.default;
      accounts[`receiver${i + 1}NftTokenAccount`] = PublicKey.default;
      accounts[`nftMetadata${i + 1}`] = PublicKey.default;
      accounts[`nftMasterEdition${i + 1}`] = PublicKey.default;
    }
  }

  return accounts;
}

export function buildClaimFundBackAccounts(params: {
  wallet: PublicKey;
  groupOptions: { buyer: PublicKey; blockhashHash: BlockhashHash };
}) {
  if (!params?.groupOptions?.blockhashHash || !params?.groupOptions?.buyer) {
    throw new Error(
      '[buildClaimFundBackAccounts] Missing blockhashHash hoặc buyer cho buy_tickets_auto PDA',
    );
  }
  if (!params.wallet.equals(params.groupOptions.buyer)) {
    throw new Error(
      '[buildClaimFundBackAccounts] Wallet must be the group owner (buyer)',
    );
  }

  const [globalStatePDA] = solanaPDA.getGlobalStatePDA();
  const [groupStatePDA] = solanaPDA.getGroupStatePDAForAuto(
    params.groupOptions.buyer,
    params.groupOptions.blockhashHash,
  );
  const [jackpotVaultPDA] = solanaPDA.getJackpotVaultPDA();

  return {
    globalState: globalStatePDA,
    groupState: groupStatePDA,
    groupOwner: params.wallet,
    jackpotVault: jackpotVaultPDA,
    systemProgram: SystemProgram.programId,
  };
}

export function buildAddWhitelisterAccounts(params: {
  wallet: PublicKey;
  groupOptions: { buyer: PublicKey; blockhashHash: BlockhashHash };
}) {
  if (!params?.groupOptions?.blockhashHash || !params?.groupOptions?.buyer) {
    throw new Error(
      '[buildAddWhitelisterAccounts] Missing blockhashHash hoặc buyer cho buy_tickets_auto PDA',
    );
  }
  const [globalStatePDA] = solanaPDA.getGlobalStatePDA();
  const [groupStatePDA] = solanaPDA.getGroupStatePDAForAuto(
    params.groupOptions.buyer,
    params.groupOptions.blockhashHash,
  );
  return {
    globalState: globalStatePDA,
    groupState: groupStatePDA,
    authority: params.wallet,
  };
}

export const buildRemoveWhitelisterAccounts = buildAddWhitelisterAccounts;

export function buildAddManyWhitelistersAccounts(params: {
  wallet: PublicKey;
  groupOptions: { buyer: PublicKey; blockhashHash: BlockhashHash };
}) {
  return buildAddWhitelisterAccounts(params);
}

export const buildRemoveManyWhitelistersAccounts = buildAddWhitelisterAccounts;

export function buildClaimLocalJackpotAccounts(params: {
  wallet: PublicKey;
  groupOptions: { buyer: PublicKey; blockhashHash: BlockhashHash };
  opts: {
    extraTokenMint: PublicKey;
    vaultAuthority: PublicKey;
    tokenVaultAta: PublicKey;
    winnerTokenAta: PublicKey;
    winningNftMint: PublicKey;
    winnerNftTokenAccount: PublicKey;
  };
}) {
  if (!params?.groupOptions?.blockhashHash || !params?.groupOptions?.buyer) {
    throw new Error(
      '[buildClaimLocalJackpotAccounts] Missing blockhashHash hoặc buyer cho buy_tickets_auto PDA',
    );
  }
  const [globalStatePDA] = solanaPDA.getGlobalStatePDA();
  const [groupStatePDA] = solanaPDA.getGroupStatePDAForAuto(
    params.groupOptions.buyer,
    params.groupOptions.blockhashHash,
  );
  return {
    globalState: globalStatePDA,
    groupState: groupStatePDA,
    winner: params.wallet,
    extraTokenMint: params.opts.extraTokenMint,
    vaultAuthority: params.opts.vaultAuthority,
    tokenVaultAta: params.opts.tokenVaultAta,
    winnerTokenAta: params.opts.winnerTokenAta,
    winningNftMint: params.opts.winningNftMint,
    winnerNftTokenAccount: params.opts.winnerNftTokenAccount,
    tokenProgram: TOKEN_PROGRAM_ID,
    associatedTokenProgram: ASSOCIATED_TOKEN_PROGRAM_ID,
    systemProgram: SystemProgram.programId,
  };
}

export function buildClaimGlobalJackpotAccounts(params: {
  wallet: PublicKey;
  purchaser: PublicKey;
  winningNftMint: PublicKey;
  winnerNftTokenAccount: PublicKey;
  groupState?: PublicKey;
  groupOptions?: { buyer: PublicKey; blockhashHash: BlockhashHash };
}) {
  const [globalStatePDA] = solanaPDA.getGlobalStatePDA();
  const [jackpotVaultPDA] = solanaPDA.getJackpotVaultPDA();
  let groupState = params.groupState;
  if (!groupState && params.groupOptions) {
    [groupState] = solanaPDA.getGroupStatePDAForAuto(
      params.groupOptions.buyer,
      params.groupOptions.blockhashHash,
    );
  }
  return {
    globalState: globalStatePDA,
    winner: params.wallet,
    purchaser: params.purchaser,
    jackpotVault: jackpotVaultPDA,
    winningNftMint: params.winningNftMint,
    winnerNftTokenAccount: params.winnerNftTokenAccount,
    ...(groupState ? { groupState } : {}),
    systemProgram: SystemProgram.programId,
  };
}
